/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useMemo } from 'react';
// material
import Space from 'material/space/space';
import { CopySnippetField, Icon, Radio, Tip, SingleSelectDropDown, TextField, CheckboxField } from '@insticator/insticator-ui';
import { SectionGroupV2 } from 'material/expandablesection/v2'
// styles
import injectSheet from 'react-jss'
import styles from './placement_styles'

const selectorModeMap = {
  CSS: {value: 'CSS', name: 'CSS Selector'},
  XPATH: {value: 'XPATH', name: 'XPath'}
}

const selectorModeOptions = [selectorModeMap.CSS, selectorModeMap.XPATH]

const placementModeMap = {
  DYNAMIC: {value: 'DYNAMIC', name: 'Dynamic'},
  HARDCODE: {value: 'HARDCODE', name: 'Hardcode'},
  HARDCODE_INFINITE: {value: 'HARDCODE_INFINITE', name: 'Hardcode - Infinite Scroll'}
}

const placementModeOptions = [placementModeMap.DYNAMIC, placementModeMap.HARDCODE, placementModeMap.HARDCODE_INFINITE]

/* ========== ~~~~~~~~~~ PRODUCT MANAGER: SITE SETUP : FEATURES ~~~~~~~~~~ ========== */
const Placement = injectSheet(styles)((props) => {

  const { groupId, infiniteScrollEnabled, dynamicPlacement, selectorMode, selector, urlFilter, onChange } = props

  const bodyCode = `<div class="insticator-product-group" data-insticator-group-id="${groupId}"></div><script>Insticator.load("insticator-product-group", { id: "${groupId}" })</script>`
  const bodyCodeInfiniteScrollEnabled = `<div class="insticator-product-group" data-insticator-group-id="${groupId}"></div>
  <script>Insticator.load("insticator-product-group", {
      id: "${groupId}",
      pageId: "{pageId}",
      pageUrl: "{pageUrl}",
      pageTitle: "{pageTitle}"
    })
  </script>`

  const selectedPlacementMode = dynamicPlacement ? 'DYNAMIC': (infiniteScrollEnabled ? 'HARDCODE_INFINITE' : 'HARDCODE')

  const placementTip = useMemo(() => {
    if(selectedPlacementMode === 'DYNAMIC') {
      return  <Tip 
        space={'none'}
        icon={'question-circle'} 
        content={`
          Insert a group of products into a specific location on your site dynamically by filling out the fields below. 
          No body code will need to be integrated manually in this case.
        `} 
        label={''} 
      />
    }
    if(selectedPlacementMode === 'HARDCODE') {
      return <Tip 
        space={'none'} 
        icon={'question-circle'} 
        content={
          <span>
          <p>
            Place this code directly into the {'<body>'} element of your page where you want this product group to show. 
            Use this option in the event of a custom site integration or if there are concerns about dynamic product insertion.
          </p>
          {!groupId ? <><br /><p>Save your draft to access the body code.</p></> : ''}
          </span>
        } 
        label={''} 
      />
    }
    if(selectedPlacementMode === 'HARDCODE_INFINITE') {
      return <Tip 
        space={'none'} 
        icon={'question-circle'} 
        content={
          <span>
          <p>
            Place this code directly into the {'<body>'} element of your page where you want this product group to show. 
            Pass data for page id, title, and URL to ensure infinite scroll capability. 
            This body code is slightly different than the standard hardcode. Infinite scroll setups cannot use dynamic product insertion.
          </p>
          {!groupId ? <><br /><p>Save your draft to access the body code.</p></> : ''}
          </span>
        } 
        label={''} 
      />
    }
  }, [selectedPlacementMode])

  return (
    <SectionGroupV2 
      klass={`placement ${props.classes.placement}`} 
      title={'Placement'} 
      description={'Define where these products will display on your web page.'}
      direction={'column'}
    >
      {false && <Space alignItems={'center'}>
        <CheckboxField 
          type={`checkbox`} 
          name={`infiniteScrollEnabled`} 
          value={infiniteScrollEnabled} 
          checked={infiniteScrollEnabled} 
          valid
          label={<span>
            Infinite scroll {' '}
            <Tip 
              space={'none'} 
              icon={'question-circle'} 
              content={
                <div>
                  <p>Enable this setting if your website loads a new article into the page automatically when the 
                user scrolls to the end of the previous article.</p>
                <br />
                <p>
                We cannot dynamically insert products on an infinite scroll setup. You must use the hardcode option and place the body code directly in your page. The body code is slightly different when infinite scroll setup is selected.
                </p>
                </div>
              } 
              label={''} 
            />
          </span>} 
          handleChange={() => onChange('infiniteScrollEnabled', !infiniteScrollEnabled)} 
        />
        <Space gap={16}>
          <Radio 
            isList
            title={<span>Dynamic{' '} 
              <Tip 
                space={'none'}
                icon={'question-circle'} 
                content={`
                  Insert a group of products into a specific location on your site dynamically by filling out the fields below. 
                  No body code will need to be integrated manually in this case.
                `} 
                label={''} 
              />
            </span>}
            checked={dynamicPlacement}
            disabled={infiniteScrollEnabled}
            handleChange={() => onChange('dynamicPlacement', true)}
          />
          <Radio 
            isList
            title={
            <span>Hardcode{' '} 
              <Tip 
                space={'none'} 
                icon={'question-circle'} 
                content={
                  <span>
                  <p>
                    This body code can be directly implemented in your article page if there's a need for custom integrations
                    or as a fallback in the event we don’t want to dynamically insert products on the page.
                  </p>
                  {!groupId ? <><br /><p>Save your draft to access the body code</p></> : ''}
                  </span>
                } 
                label={''} 
              />
              </span>}
            checked={!dynamicPlacement}
            handleChange={() => onChange('dynamicPlacement', false)}
          />
        </Space>
      </Space>}
      <Space direction={'column'}>
        <Space alignItems={'center'}>
          <SingleSelectDropDown 
            name={placementModeMap[selectedPlacementMode || 'DYNAMIC'].name} 
            searchable={false} 
            onSelect={opt => {
              if(opt.value === 'DYNAMIC') onChange('dynamicPlacement', true)
              if(opt.value === 'HARDCODE') onChange('infiniteScrollEnabled', false)
              if(opt.value === 'HARDCODE_INFINITE') onChange('infiniteScrollEnabled', true)
            }} 
            items={placementModeOptions}
          />
          {placementTip}
        </Space>

        {!dynamicPlacement ?
         <Space klass={`disabled-${!groupId}`} direction={'column'}>
          <CopySnippetField
            codeSnippet={infiniteScrollEnabled ? bodyCodeInfiniteScrollEnabled : bodyCode}
            inline
          />
        </Space>
        :
        <>
        <Space alignItems={'center'}>
          <SingleSelectDropDown 
            name={selectorModeMap[selectorMode || 'CSS'].name} 
            searchable={false} 
            onSelect={opt => onChange('selectorMode', opt.value)} 
            items={selectorModeOptions}
          />
          <TextField 
            type={`text`} 
            name={`selector`} 
            value={selector} 
            handleChange={e => { onChange?.('selector', e.target.value)}} 
            valid={true} 
            label={''}
            placeholder={`${selectorMode === 'CSS' ? 'Enter css selector' : 'Enter xPath selector'}`} 
            maxLength={256}
          />
          <Tip 
            space={'none'} 
            icon={'question-circle'} 
            content={`
              Get the CSS selector or XPath code and paste it below. Navigate to your site. Then click on any article. 
              Find the placement in that article you want the product grouping to appear. Right click and “Inspect”. 
              Copy the CSS selector or XPath code of that element and paste it here.              
            `} 
            label={''} 
          />
        </Space>
        <Space alignItems={'center'}>
          <TextField 
            type={`text`} 
            name={`urlfilter`} 
            value={urlFilter} 
            handleChange={e => { onChange?.('urlFilter', e.target.value)}} 
            valid={true} 
            label={''}
            placeholder={`Enter expression`} 
            maxLength={512}
          />
          <Tip 
            space={'none'} 
            icon={'question-circle'} 
            content={
              <span>Add a <a target="_blank" href="https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_expressions" rel="noreferrer">regular expression</a> to 
              target the specific pages you want this product to be inserted on. 
              Use this <a target='_blank' href="https://regexr.com/" rel="noreferrer">helper tool</a> to test your expressions.
              </span>
            } 
            label={''} 
          />
        </Space>
        </>
        }

      </Space>
    </SectionGroupV2>
  )
})

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default Placement;